import { createSelector, createSlice, PayloadAction } from "@reduxjs/toolkit";
import { RootState } from "../../app/Store";
import { ReplacementState, ReplacementType } from "./ReplacementModels";

interface IReplacementDialogState {
  isOpen: boolean;
  replacementState: ReplacementState;
  serialNumber: string;
  reason: string | null;
  replacementType: ReplacementType | null;
  showDriveUnitReplacementTypeSelector: boolean;
  trackerId: number | null;
  errorMessage: string | null;
  requestId?: string;
}

interface IMultiSiteReplacementDialogState {
  [siteId: string]: IReplacementDialogState;
}

const getDefaultDialogState = (): IReplacementDialogState => ({
  isOpen: false,
  replacementState: ReplacementState.Form,
  serialNumber: "",
  reason: null,
  replacementType: null,
  showDriveUnitReplacementTypeSelector: false,
  trackerId: null,
  errorMessage: null,
});

const initialState: IMultiSiteReplacementDialogState = {};

export const replacementDialogSlice = createSlice({
  name: "replacementDialog",
  initialState,
  reducers: {
    openReplacementDialog: (
      state,
      action: PayloadAction<{
        site: string;
        replacementType: ReplacementType;
        trackerId: number;
      }>
    ) => {
      state[action.payload.site] = {
        ...getDefaultDialogState(),
        isOpen: true,
        replacementType:
          action.payload.replacementType === ReplacementType.PCB ? ReplacementType.PCB : null,
        showDriveUnitReplacementTypeSelector:
          action.payload.replacementType === ReplacementType.DriveUnit,
        trackerId: action.payload.trackerId,
      };
    },
    closeReplacementDialog: (state, action: PayloadAction<{ site: string }>) => {
      if (state[action.payload.site]) {
        state[action.payload.site] = getDefaultDialogState();
      }
    },
    setReplacementState: (
      state,
      action: PayloadAction<{
        site: string;
        requestId: string;
        replacementState: ReplacementState;
        errorMessage?: string;
      }>
    ) => {
      const { site, requestId, replacementState, errorMessage } = action.payload;
      const dialog = state[site];
      if (!dialog?.isOpen) {
        return;
      }
      if (replacementState === ReplacementState.Loading) {
        dialog.requestId = requestId;
      } else if (dialog.requestId !== requestId) {
        return;
      } else {
        delete dialog.requestId;
      }
      dialog.replacementState = replacementState;
      dialog.errorMessage = replacementState === ReplacementState.Error ? errorMessage || null : null;
    },
    setSerialNumber: (state, action: PayloadAction<{ site: string; serialNumber: string }>) => {
      if (state[action.payload.site]) {
        state[action.payload.site].serialNumber = action.payload.serialNumber;
      }
    },
    setReplacementReason: (state, action: PayloadAction<{ site: string; reason: string | null }>) => {
      if (state[action.payload.site]) {
        state[action.payload.site].reason = action.payload.reason;
      }
    },
    setReplacementType: (
      state,
      action: PayloadAction<{ site: string; replacementType: ReplacementType }>
    ) => {
      if (state[action.payload.site]) {
        state[action.payload.site].replacementType = action.payload.replacementType;
      }
    },
  },
});

export const selectCurrentReplacementDialogState = createSelector(
  [
    (state: RootState) => state.trackerComponentReplacementDialog,
    (state: RootState) => state.site.siteId,
  ],
  (multiSiteDialogState: IMultiSiteReplacementDialogState, currentSite: string) => {
    return multiSiteDialogState[currentSite] || getDefaultDialogState();
  }
);

export const trackerComponentReplacementDialogReducer = replacementDialogSlice.reducer;
